import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './mockClient'
import type {
  AccountantExport,
  AiSuggestion,
  AuditLogEntry,
  AuthSession,
  BankTransaction,
  BankingImportResponse,
  BreakdownResponse,
  BreakdownTrendResponse,
  Company,
  CompanySettings,
  CostAllocationPayload,
  CreateUserPayload,
  CronJobStatus,
  CronRun,
  HealthIntegrationsResponse,
  DiscrepancyType,
  EmailAccountSettings,
  InvoiceComment,
  InvoiceDetail,
  InvoiceListItem,
  ListInvoicesParams,
  NavCredentials,
  OutgoingEmailSettings,
  PaginatedResponse,
  Role,
  UpdateCompanySettingsPayload,
  UpdateEmailAccountPayload,
  UpdateNavCredentialsPayload,
  UpdateOutgoingEmailPayload,
  UploadInvoicePayload,
  UpsertCompanyPayload,
  User,
  UserListItem,
  WorkflowStatus,
} from './types'

export const queryKeys = {
  companies: ['companies'] as const,
  invoices: (params: ListInvoicesParams) => ['invoices', params] as const,
  invoice: (id: string) => ['invoice', id] as const,
  invoicePdf: (id: string) => ['invoice-pdf', id] as const,
  discrepancies: (type?: DiscrepancyType) => ['discrepancies', type ?? 'all'] as const,
  vatSummary: (companyId: string, year: number, month: number) =>
    ['vat-summary', companyId, year, month] as const,
  monthlyExport: (companyId: string, year: number, month: number) =>
    ['monthly-export', companyId, year, month] as const,
  notifications: (unreadOnly: boolean) => ['notifications', unreadOnly] as const,
  jobs: ['jobs'] as const,
  inbox: ['inbox'] as const,
  invoiceComments: (id: string) => ['invoice-comments', id] as const,
  users: ['users'] as const,
  navCredentials: (companyId: string) => ['nav-credentials', companyId] as const,
  emailAccount: ['email-account'] as const,
  companySettings: (companyId: string) => ['company-settings', companyId] as const,
  auditLogs: (subjectType?: string) => ['audit-logs', subjectType ?? 'all'] as const,
  breakdown: (companyId: string, year: number, month: number) =>
    ['breakdown', companyId, year, month] as const,
}

export function useCompanies() {
  return useQuery<Company[]>({ queryKey: queryKeys.companies, queryFn: () => api.listCompanies() })
}

export interface InvoiceStats {
  total: number
  bySource: Partial<Record<'nav' | 'email' | 'manual' | 'szamlazz', number>>
  byWorkflowStatus: Record<string, number>
  lastNavSyncAt: string | null
}

/** Aggregátum a cég számláin — NavSyncStatusPanel és dashboard használja. */
export function useInvoiceStats(companyId: string | null | undefined) {
  return useQuery<InvoiceStats>({
    queryKey: ['invoice-stats', companyId],
    queryFn: () => api.getInvoiceStats!(companyId!),
    enabled: !!companyId,
    staleTime: 30_000,
  })
}

// ── AI hookok ─────────────────────────────────────────────────────────────

export function useAiStatus() {
  return useQuery<{ enabled: boolean }>({
    queryKey: ['ai', 'status'],
    queryFn: () => api.aiStatus!(),
    staleTime: 60_000,
  })
}

export function useAiInvoiceSuggestions(invoiceId: string | null | undefined) {
  return useQuery<{ suggestions: AiSuggestion[] }>({
    queryKey: ['ai', 'invoice-suggestions', invoiceId],
    queryFn: () => api.aiListInvoiceSuggestions!(invoiceId!),
    enabled: !!invoiceId,
    staleTime: 10_000,
  })
}

export function useAiAnalyzeInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => api.aiAnalyzeInvoice!(invoiceId),
    onSuccess: (_data, invoiceId) => {
      qc.invalidateQueries({ queryKey: ['ai', 'invoice-suggestions', invoiceId] })
      qc.invalidateQueries({ queryKey: ['invoice', invoiceId] })
    },
  })
}

export function useAiAcceptSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.aiAcceptSuggestion!(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai', 'invoice-suggestions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice'] })
    },
  })
}

export function useAiRejectSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.aiRejectSuggestion!(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai', 'invoice-suggestions'] }),
  })
}

export function useAiMonthlySummary(year: number, month: number, companyId: string | null | undefined) {
  return useQuery<{ result: AiSuggestion | null }>({
    queryKey: ['ai', 'monthly-summary', year, month, companyId],
    queryFn: () => api.aiGetMonthlySummary!(year, month, companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  })
}

export function useAiGenerateMonthlySummary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ year, month, companyId }: { year: number; month: number; companyId: string }) =>
      api.aiGenerateMonthlySummary!(year, month, companyId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['ai', 'monthly-summary', vars.year, vars.month, vars.companyId] })
    },
  })
}

export function useAiCashflow(companyId: string | null | undefined) {
  return useQuery<{ result: AiSuggestion | null }>({
    queryKey: ['ai', 'cashflow', companyId],
    queryFn: () => api.aiGetCashflow!(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  })
}

export function useAiGenerateCashflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (companyId: string) => api.aiGenerateCashflow!(companyId),
    onSuccess: (_d, companyId) => qc.invalidateQueries({ queryKey: ['ai', 'cashflow', companyId] }),
  })
}

export function useAiAccountantPrep(year: number, month: number, companyId: string | null | undefined) {
  return useQuery<{ result: AiSuggestion | null }>({
    queryKey: ['ai', 'accountant-prep', year, month, companyId],
    queryFn: () => api.aiGetAccountantPrep!(year, month, companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  })
}

export function useAiGenerateAccountantPrep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ year, month, companyId }: { year: number; month: number; companyId: string }) =>
      api.aiGenerateAccountantPrep!(year, month, companyId),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ['ai', 'accountant-prep', v.year, v.month, v.companyId] }),
  })
}

export function useUpsertCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpsertCompanyPayload) => api.upsertCompany(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.companies })
      qc.invalidateQueries({ queryKey: queryKeys.auditLogs() })
    },
  })
}

// --- Számlázz.hu hookok ---

export function useSzamlazz(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['szamlazz', companyId ?? ''],
    queryFn: () => (api as unknown as { getSzamlazz: (id: string) => Promise<unknown> }).getSzamlazz(companyId!),
    enabled: !!companyId,
  })
}

export function useUpdateSzamlazz() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, payload }: { companyId: string; payload: { agentKey?: string; enabled?: boolean } }) =>
      (api as unknown as { updateSzamlazz: (id: string, p: unknown) => Promise<unknown> }).updateSzamlazz(companyId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['szamlazz', vars.companyId] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useTestSzamlazz() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (companyId: string) =>
      (api as unknown as { testSzamlazz: (id: string) => Promise<{ ok: boolean; detail: string }> }).testSzamlazz(companyId),
    onSuccess: (_d, companyId) => {
      qc.invalidateQueries({ queryKey: ['szamlazz', companyId] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
    onError: (_e, companyId) => {
      qc.invalidateQueries({ queryKey: ['szamlazz', companyId] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useSyncSzamlazz() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (companyId: string) =>
      (api as unknown as { syncSzamlazz: (id: string) => Promise<{ jobId: string; status: string; detail: string }> }).syncSzamlazz(companyId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useAuditLogs(subjectType?: string) {
  return useQuery<AuditLogEntry[]>({
    queryKey: queryKeys.auditLogs(subjectType),
    queryFn: () => api.listAuditLogs({ subjectType, limit: 200 }),
    refetchInterval: 5000,
  })
}

export function useInvoices(params: ListInvoicesParams) {
  return useQuery<PaginatedResponse<InvoiceListItem>>({
    queryKey: queryKeys.invoices(params),
    queryFn: () => api.listInvoices(params),
  })
}

export function useInvoice(id: string | undefined) {
  return useQuery<InvoiceDetail>({
    queryKey: queryKeys.invoice(id ?? ''),
    queryFn: () => api.getInvoice(id!),
    enabled: !!id,
  })
}

export function useInvoicePdf(id: string | undefined, hasPdf: boolean) {
  return useQuery<Uint8Array>({
    queryKey: queryKeys.invoicePdf(id ?? ''),
    queryFn: () => api.getInvoicePdf(id!),
    enabled: !!id && hasPdf,
    staleTime: 5 * 60_000,
    // A bytes nem-szerializálható, ezért nem mentjük el persistert
    gcTime: 5 * 60_000,
  })
}

export function useApproveInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) => api.approveInvoice(id, comment),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoice(vars.id) })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUpdateInvoiceStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, toStatus, reason }: { id: string; toStatus: WorkflowStatus; reason?: string }) =>
      api.updateInvoiceStatus(id, toStatus, reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoice(vars.id) })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useRecordPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      invoiceId,
      payload,
    }: {
      invoiceId: string
      payload: {
        status: 'paid' | 'partially_paid' | 'unpaid' | 'overdue'
        paidAmount?: number
        paidAt?: string
        method?: 'bank_transfer' | 'cash' | 'card' | 'other'
        reference?: string
      }
    }) => (api as unknown as { recordPayment: typeof api.recordPayment }).recordPayment(invoiceId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoice(vars.invoiceId) })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useUpdateCostAllocation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, payload }: { invoiceId: string; payload: CostAllocationPayload }) =>
      (api as unknown as { updateCostAllocation: typeof api.updateCostAllocation }).updateCostAllocation(
        invoiceId,
        payload,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoice(vars.invoiceId) })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['breakdown'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useCronStatus() {
  return useQuery<{ jobs: CronJobStatus[] }>({
    queryKey: ['cron-status'],
    queryFn: () => (api as unknown as { getCronStatus: typeof api.getCronStatus }).getCronStatus(),
    refetchInterval: 15_000, // 15 mp-enként frissítjük, hogy a "futás" élben látszódjon
  })
}

export function useBankTransactions(companyId: string | null | undefined, unmatched = false) {
  return useQuery<{ items: BankTransaction[] }>({
    queryKey: ['bank-transactions', companyId ?? '_none', unmatched],
    queryFn: () =>
      (api as unknown as { listBankTransactions: typeof api.listBankTransactions }).listBankTransactions(
        companyId!,
        unmatched,
      ),
    enabled: !!companyId,
  })
}

export function useImportBankCsv() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, file }: { companyId: string; file: File }) =>
      (api as unknown as { importBankCsv: typeof api.importBankCsv }).importBankCsv(companyId, file) as Promise<BankingImportResponse>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export interface BankPdfImportResponse extends BankingImportResponse {
  bankName?: string
  aiEnriched?: number
  metadata?: {
    kivonatSzam: string | null
    szamlaszam: string | null
    idoszak: string | null
    nyitoEgyenleg: number | null
    zaroEgyenleg: number | null
    terhelesekOsszesen: number | null
    jovairasokOsszesen: number | null
  }
  diagnostics?: { textSample: string; lineCount: number } | null
}

export function useImportBankPdf() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, file }: { companyId: string; file: File }) =>
      (api as unknown as { importBankPdf: (c: string, f: File) => Promise<BankPdfImportResponse> }).importBankPdf(
        companyId,
        file,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useMatchBankTransaction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ transactionId, invoiceId }: { transactionId: string; invoiceId: string }) =>
      (api as unknown as { manualMatchBankTransaction: typeof api.manualMatchBankTransaction }).manualMatchBankTransaction(
        transactionId,
        invoiceId,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUnmatchBankTransaction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (transactionId: string) =>
      (api as unknown as { unmatchBankTransaction: typeof api.unmatchBankTransaction }).unmatchBankTransaction(
        transactionId,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useDeleteBankTransaction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (transactionId: string) =>
      (api as unknown as { deleteBankTransaction: typeof api.deleteBankTransaction }).deleteBankTransaction(
        transactionId,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useDeleteBankTransactions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (transactionIds: string[]) =>
      (api as unknown as { deleteBankTransactions: typeof api.deleteBankTransactions }).deleteBankTransactions(
        transactionIds,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useRematchBankTransactions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      (api as unknown as { rematchBankTransactions: typeof api.rematchBankTransactions }).rematchBankTransactions(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useHealthIntegrations() {
  return useQuery<HealthIntegrationsResponse>({
    queryKey: ['health-integrations'],
    queryFn: () =>
      (api as unknown as { getHealthIntegrations: typeof api.getHealthIntegrations }).getHealthIntegrations(),
    refetchInterval: 30_000,
  })
}

export function useCronRuns(limit = 30) {
  return useQuery<{ runs: CronRun[] }>({
    queryKey: ['cron-runs', limit],
    queryFn: () => (api as unknown as { getCronRuns: typeof api.getCronRuns }).getCronRuns(limit),
    refetchInterval: 30_000,
  })
}

export function useRunCronJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (type: string) =>
      (api as unknown as { runCronJob: typeof api.runCronJob }).runCronJob(type),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cron-status'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useBulkInvoiceAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      ids: string[]
      action: 'addTag' | 'removeTag' | 'setProjectCode' | 'setCategory' | 'archive' | 'approve' | 'delete'
      value?: string | null
    }) => (api as unknown as { bulkInvoiceAction: typeof api.bulkInvoiceAction }).bulkInvoiceAction(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['breakdown'] })
      qc.invalidateQueries({ queryKey: ['audit-logs'] })
    },
  })
}

export function useBreakdown(companyId: string | null | undefined, year: number, month: number) {
  return useQuery<BreakdownResponse>({
    queryKey: queryKeys.breakdown(companyId ?? '_none', year, month),
    queryFn: () =>
      (api as unknown as { getBreakdown: typeof api.getBreakdown }).getBreakdown(companyId!, year, month),
    enabled: !!companyId,
  })
}

export function useBreakdownTrend(companyId: string | null | undefined, months = 12) {
  return useQuery<BreakdownTrendResponse>({
    queryKey: ['breakdown-trend', companyId ?? '_none', months],
    queryFn: () =>
      (api as unknown as { getBreakdownTrend: typeof api.getBreakdownTrend }).getBreakdownTrend(
        companyId!,
        months,
      ),
    enabled: !!companyId,
  })
}

export function useArchiveInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.archiveInvoice(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUploadInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UploadInvoicePayload) => api.uploadInvoice(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useDiscrepancies(type?: DiscrepancyType) {
  return useQuery({
    queryKey: queryKeys.discrepancies(type),
    queryFn: () => api.listDiscrepancies(type),
  })
}

export function useVatMonthlySummary(companyId: string, year: number, month: number) {
  return useQuery({
    queryKey: queryKeys.vatSummary(companyId, year, month),
    queryFn: () => api.getVatMonthlySummary(companyId, year, month),
  })
}

export function useMonthlyExport(companyId: string, year: number, month: number) {
  return useQuery<AccountantExport>({
    queryKey: queryKeys.monthlyExport(companyId, year, month),
    queryFn: () => api.getMonthlyExport(companyId, year, month),
  })
}

export function useApproveExport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) => api.approveExport(id, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['monthly-export'] }),
  })
}

export function useRejectExport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.rejectExport(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['monthly-export'] }),
  })
}

export function useSendExport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) => api.sendExport(id, to),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['monthly-export'] }),
  })
}

export function useNotifications(unreadOnly = false) {
  return useQuery({
    queryKey: queryKeys.notifications(unreadOnly),
    queryFn: () => api.listNotifications(unreadOnly),
  })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
}

export function useJobs() {
  return useQuery({ queryKey: queryKeys.jobs, queryFn: () => api.listJobs() })
}

export function useInbox() {
  return useQuery({ queryKey: queryKeys.inbox, queryFn: () => api.listInbox() })
}

export function useAllTags() {
  return useQuery<string[]>({
    queryKey: ['all-tags'],
    queryFn: () => api.listAllTags(),
  })
}

export function useAddInvoiceTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, tag }: { invoiceId: string; tag: string }) =>
      api.addInvoiceTag(invoiceId, tag),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoice(vars.invoiceId) })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['all-tags'] })
    },
  })
}

export function useRemoveInvoiceTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, tag }: { invoiceId: string; tag: string }) =>
      api.removeInvoiceTag(invoiceId, tag),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoice(vars.invoiceId) })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['all-tags'] })
    },
  })
}

export function useInvoiceComments(id: string | undefined) {
  return useQuery<InvoiceComment[]>({
    queryKey: queryKeys.invoiceComments(id ?? ''),
    queryFn: () => api.listInvoiceComments(id!),
    enabled: !!id,
  })
}

export function useAddInvoiceComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      body,
      author,
      authorRole,
    }: {
      id: string
      body: string
      author: User
      authorRole: Role | null
    }) => api.addInvoiceComment(id, body, author, authorRole),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoiceComments(vars.id) })
    },
  })
}

export function useLogin() {
  return useMutation<AuthSession, Error, { email: string; password: string }>({
    mutationFn: ({ email, password }) => api.login(email, password),
  })
}

export function useNeedsSetup() {
  return useQuery<boolean>({
    queryKey: ['needs-setup'],
    queryFn: () => api.needsSetup(),
    staleTime: 0,
  })
}

export function useSetupInitialAdmin() {
  const qc = useQueryClient()
  return useMutation<
    AuthSession,
    Error,
    {
      fullName: string
      email: string
      password: string
      companyLegalName: string
      companyTaxNumber: string
      companyCountryCode?: string
      companyCurrency?: string
    }
  >({
    mutationFn: (payload) => api.setupInitialAdmin(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['needs-setup'] })
      qc.invalidateQueries({ queryKey: queryKeys.users })
      qc.invalidateQueries({ queryKey: queryKeys.companies })
    },
  })
}

export function useUsers() {
  return useQuery<UserListItem[]>({
    queryKey: queryKeys.users,
    queryFn: () => api.listUsers(),
  })
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateUserPayload) => api.createUser(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users })
    },
  })
}

// --- Settings hookok ---

export function useNavCredentials(companyId: string | null | undefined) {
  return useQuery<NavCredentials | null>({
    queryKey: queryKeys.navCredentials(companyId ?? ''),
    queryFn: () => api.getNavCredentials(companyId!),
    enabled: !!companyId,
  })
}

export function useUpdateNavCredentials() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateNavCredentialsPayload) => api.updateNavCredentials(payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.navCredentials(vars.companyId) })
    },
  })
}

export function useTestNavConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (companyId: string) => api.testNavConnection(companyId),
    onSuccess: (_data, companyId) => {
      qc.invalidateQueries({ queryKey: queryKeys.navCredentials(companyId) })
      qc.invalidateQueries({ queryKey: queryKeys.auditLogs('nav_credentials') })
    },
    onError: (_err, companyId) => {
      // A mockClient hiba esetén is frissíti a `lastPingStatus`-t és ír auditot,
      // ezért a query-ket itt is meg kell invalidálni.
      qc.invalidateQueries({ queryKey: queryKeys.navCredentials(companyId) })
      qc.invalidateQueries({ queryKey: queryKeys.auditLogs('nav_credentials') })
    },
  })
}

export function useOutgoingEmail() {
  return useQuery<OutgoingEmailSettings>({
    queryKey: ['outgoing-email'],
    queryFn: () => api.getOutgoingEmail(),
  })
}

export function useUpdateOutgoingEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateOutgoingEmailPayload) => api.updateOutgoingEmail(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outgoing-email'] })
      qc.invalidateQueries({ queryKey: queryKeys.auditLogs('outgoing_email') })
    },
  })
}

export function useTestOutgoingEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.testOutgoingEmail(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outgoing-email'] })
      qc.invalidateQueries({ queryKey: queryKeys.auditLogs('outgoing_email') })
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: ['outgoing-email'] })
      qc.invalidateQueries({ queryKey: queryKeys.auditLogs('outgoing_email') })
    },
  })
}

export function useEmailAccount() {
  return useQuery<EmailAccountSettings>({
    queryKey: queryKeys.emailAccount,
    queryFn: () => api.getEmailAccount(),
  })
}

export function useUpdateEmailAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateEmailAccountPayload) => api.updateEmailAccount(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.emailAccount }),
  })
}

export function useTestEmailConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.testEmailConnection(),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.emailAccount }),
  })
}

export function useListImapFolders() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.listImapFolders(),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.emailAccount }),
  })
}

export function useCompanySettings(companyId: string | null | undefined) {
  return useQuery<CompanySettings | null>({
    queryKey: queryKeys.companySettings(companyId ?? ''),
    queryFn: () => api.getCompanySettings(companyId!),
    enabled: !!companyId,
  })
}

export function useUpdateCompanySettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, payload }: { companyId: string; payload: UpdateCompanySettingsPayload }) =>
      api.updateCompanySettings(companyId, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.companySettings(vars.companyId) })
    },
  })
}
